import React, { memo, useCallback } from 'react'

import i18n from '@batical/i18n'
import { useContextValue } from '@batical/reducer/src'
import { CalculCodeStateKeys } from '@batical/types/src'
import { Select, MenuItem, FormControl } from '@mui/material'

import { batikalColor } from '../Colors'
import { CalculText, ViewLine } from './LineStyleComponent'

export type PickerLineProps = {
  calculCode: CalculCodeStateKeys
  dataPicker: Array<{ label: string; value: string }>
  subtitle?: string
  withoutTitle?: boolean //use for native
  withTitle?: boolean //use for web
  variableName: string
  disabled?: boolean
  onChange?: (item: string) => void
  first?: boolean
  last?: boolean
}
const PickerLine = ({
  calculCode,
  dataPicker,
  withTitle = false,
  variableName,
  onChange,
  first = false,
  last = false
}: PickerLineProps) => {
  const [value, update] = useContextValue(variableName, calculCode)
  const getTitle = useCallback((): string => {
    if (withTitle) {
      return i18n.t(`${calculCode}_${variableName}`)
    }
    return ''
  }, [calculCode, variableName, withTitle])

  const _onChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      if (onChange) {
        onChange(String(event.target.value as string))
      } else {
        update(String(event.target.value as string))
      }
    },
    [onChange, update]
  )

  return (
    <ViewLine first={first} last={last}>
      {withTitle && <CalculText>{getTitle()}</CalculText>}
      <FormControl fullWidth={!withTitle} size="small" sx={{ my: 0.5 }}>
        <Select
          value={value}
          onChange={_onChange}
          fullWidth
          displayEmpty
          sx={{
            borderRadius: '8px',
            fontSize: 14,
            color: '#1e293b',
            backgroundColor: '#f8fafc',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e2e8f0',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#94a3b8',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: batikalColor,
              borderWidth: '1.5px',
            },
            '& .MuiSelect-select': {
              py: '10px',
            }
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                borderRadius: '10px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                mt: 0.5,
                '& .MuiMenuItem-root': {
                  fontSize: 14,
                  color: '#334155',
                  py: 1.2,
                  px: 2,
                  borderRadius: '6px',
                  mx: 0.5,
                  '&:hover': {
                    backgroundColor: '#f1f5f9',
                  },
                  '&.Mui-selected': {
                    backgroundColor: `${batikalColor}12`,
                    color: batikalColor,
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: `${batikalColor}18`,
                    }
                  }
                }
              }
            }
          }}>
          {dataPicker.map((item, index) => (
            <MenuItem value={item.value} key={`pickerLine_${calculCode}_${variableName}_${index}`}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </ViewLine>
  )
}

export default memo(PickerLine)
